import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useUserRole } from "@/hooks/useUserRole";

type PermissionLevel = "none" | "view" | "edit" | "manage";

const LEVEL_HIERARCHY: PermissionLevel[] = ["none", "view", "edit", "manage"];

export const PERMISSION_AREAS = [
  "bookings", "schedule", "customers", "courts", "pricing", "payments",
  "invoicing", "memberships", "leagues", "tournaments", "training",
  "community", "reports", "settings", "users", "credits",
] as const;

type _PermissionArea = typeof PERMISSION_AREAS[number];

export interface Permissions {
  [key: string]: PermissionLevel | boolean;
}

const DEFAULT_PLAYER_PERMISSIONS: Permissions = Object.fromEntries([
  ...PERMISSION_AREAS.map(a => [a, "none" as PermissionLevel]),
]);

export function usePermissions() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { isAdmin } = useUserRole();

  const { data: assignment, isLoading } = useQuery({
    queryKey: ["staff-assignment", user?.id, organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("staff_assignments" as any)
        .select("*, staff_roles(*)")
        .eq("user_id", user!.id)
        .eq("organization_id", organization!.id)
        .maybeSingle();
      return data as any;
    },
    enabled: !!user?.id && !!organization?.id,
  });

  const rolePerms: Permissions = (assignment?.staff_roles?.permissions as Permissions) ?? DEFAULT_PLAYER_PERMISSIONS;
  const roleName: string = assignment?.staff_roles?.name ?? (isAdmin ? "Admin" : "Spelare");
  const roleColor: string = assignment?.staff_roles?.color ?? (isAdmin ? "#dc2626" : "#6b7280");

  // Admin fallback: if user is admin but has no staff_assignment, grant full access
  const permissions: Permissions = isAdmin && !assignment
    ? Object.fromEntries([
        ...PERMISSION_AREAS.map(a => [a, "manage" as PermissionLevel]),
        ["can_refund_payments", true],
        ["can_cancel_bookings", true],
        ["can_export_data", true],
        ["can_manage_roles", true],
        ["can_view_revenue", true],
        ["can_impersonate", true],
      ])
    : rolePerms;

  const can = (area: string, minLevel: PermissionLevel = "view"): boolean => {
    const level = (permissions[area] as PermissionLevel) ?? "none";
    return LEVEL_HIERARCHY.indexOf(level) >= LEVEL_HIERARCHY.indexOf(minLevel);
  };

  const canDo = (permission: string): boolean => {
    return permissions[permission] === true;
  };

  return {
    permissions,
    roleName,
    roleColor,
    isLoading,
    can,
    canDo,
    isOwner: roleName === "Ägare",
    isStaff: !!assignment || isAdmin,
    PERMISSION_AREAS,
  };
}
